import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { LoadingBlock } from '../components/Spinner'
import { Avatar } from './Avatar'
import { OfHtml } from './OfHtml'
import type { OfApi } from './ofApi'
import { toComment, type Comment, type OfUser, type Post } from './posts'
import { relativeTime } from './time'

interface Props {
  post: Post
  ownerId: number
  canWrite: boolean
  of: OfApi
  referer: string
  users: Record<number, OfUser>
  resolveUsers: (ids: number[]) => void
  onCount: (n: number) => void
  say: (text: string) => void
}

type Payload = { list?: Record<string, unknown>[]; hasMore?: boolean; nextOffset?: number }

/** A post's comments: newest 3 first, "Show more" pages by 10 (OnlyFans' offsets). */
export function Comments({ post, ownerId, canWrite, of, referer, users, resolveUsers, onCount, say }: Props) {
  const { t } = useTranslation('ofPages')
  const [items, setItems] = useState<Comment[]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [menuId, setMenuId] = useState<number | null>(null)
  const input = useRef<HTMLInputElement | null>(null)
  const count = useRef(post.comments)
  count.current = post.comments

  const load = useCallback(
    async (from: number, limit: number) => {
      setLoading(true)
      try {
        const p = await of.get<Payload>(`posts/${post.id}/comments`, { limit, offset: from, sort: 'desc', skip_users: 'all', format: 'infinite' }, referer)
        const page = (p?.list ?? []).map(toComment)
        resolveUsers(page.map((c) => c.authorId))
        setItems((all) => (from === 0 ? page : [...all, ...page.filter((c) => !all.some((x) => x.id === c.id))]))
        setOffset(p?.nextOffset ?? from + page.length)
        setHasMore(p?.hasMore === true)
      } catch {
        say(t('error'))
      } finally {
        setLoading(false)
      }
    },
    [of, post.id, referer, resolveUsers, say, t],
  )

  useEffect(() => {
    void load(0, 3)
  }, [load])

  const act = async (work: () => Promise<void>) => {
    try {
      await work()
    } catch (e) {
      say(e instanceof Error && e.message ? e.message : t('error'))
    }
  }

  const send = () =>
    act(async () => {
      const body = text.trim()
      if (!body || sending) return
      setSending(true)
      try {
        const raw = await of.send<Record<string, unknown>>('POST', `posts/${post.id}/comments`, referer, { text: body, answerTo: null, giphyId: null })
        const c = toComment(raw ?? {})
        if (!c.authorId) c.authorId = ownerId
        setItems((all) => [c, ...all])
        setText('')
        onCount(count.current + 1)
      } finally {
        setSending(false)
      }
    })

  const like = (c: Comment) =>
    act(async () => {
      const r = await of.send<{ isLiked?: boolean; likesCount?: number }>(c.isLiked ? 'DELETE' : 'POST', `comments/${c.id}/like`, referer)
      const isLiked = r?.isLiked ?? !c.isLiked
      // OnlyFans answers the count from before the change.
      const likes = Math.max(0, c.likes + (isLiked ? 1 : -1))
      setItems((all) => all.map((x) => (x.id === c.id ? { ...x, isLiked, likes } : x)))
    })

  const pin = (c: Comment) =>
    act(async () => {
      setMenuId(null)
      await of.send(c.isPinned ? 'DELETE' : 'POST', `comments/${c.id}/pin`, referer)
      setItems((all) => all.map((x) => (x.id === c.id ? { ...x, isPinned: !c.isPinned } : { ...x, isPinned: c.isPinned ? x.isPinned : false })))
    })

  const remove = (c: Comment) =>
    act(async () => {
      setMenuId(null)
      await of.send('DELETE', `comments/${c.id}`, referer)
      setItems((all) => all.filter((x) => x.id !== c.id))
      onCount(Math.max(0, count.current - 1))
      say(t('comments.deleted'))
    })

  const reply = (c: Comment) => {
    const who = users[c.authorId]?.username
    setText(who ? `@${who}, ` : '')
    input.current?.focus()
  }

  const ownPost = post.authorId === ownerId
  return (
    <section className="ofp-comments" aria-label={t('post.comments')}>
      {items.map((c) => {
        const u = users[c.authorId]
        const canManage = canWrite && (ownPost || c.authorId === ownerId)
        return (
          <div key={c.id} className="ofp-comment">
            <Avatar user={u} size={32} />
            <div className="ofp-comment-body">
              <div className="ofp-comment-line">
                <span className="ofp-comment-name">{u?.name ?? ''}</span>
                {c.isPinned && <FeatherIcon name="bookmark" size={11} className="ofp-comment-pin" />}
                <OfHtml html={c.text} className="ofp-comment-text" />
              </div>
              <div className="ofp-comment-meta">
                <span>{relativeTime(c.postedAt, t)}</span>
                {c.likes > 0 && <span>{t('comments.likes', { count: c.likes })}</span>}
                {canWrite && (
                  <button type="button" className="ofp-link-btn" onClick={() => reply(c)}>
                    {t('comments.reply')}
                  </button>
                )}
                {canManage && (
                  <span className="lib-anchor">
                    <button type="button" className="ofp-link-btn" aria-label={t('comments.actions')} onClick={() => setMenuId(menuId === c.id ? null : c.id)}>
                      <FeatherIcon name="more-horizontal" size={14} />
                    </button>
                    {menuId === c.id && (
                      <>
                        <div className="lib-backdrop" onClick={() => setMenuId(null)} />
                        <div className="lib-pop lib-menu ofp-menu-left" role="menu" aria-label={t('comments.actions')}>
                          {ownPost && (
                            <button type="button" role="menuitem" className="lib-menu-item" onClick={() => void pin(c)}>
                              <span className="lib-menu-label">{c.isPinned ? t('comments.unpin') : t('comments.pin')}</span>
                            </button>
                          )}
                          <button type="button" role="menuitem" className="lib-menu-item lib-menu-item--danger" onClick={() => void remove(c)}>
                            <span className="lib-menu-label">{t('comments.delete')}</span>
                          </button>
                        </div>
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              className={`ofp-tool ofp-tool--sm ${c.isLiked ? 'is-on' : ''}`}
              aria-pressed={c.isLiked}
              aria-label={c.isLiked ? t('comments.unlike') : t('comments.like')}
              disabled={!canWrite || !c.canLike}
              onClick={() => void like(c)}
            >
              <FeatherIcon name="heart" size={14} />
            </button>
          </div>
        )
      })}
      {loading && <LoadingBlock pad="12px 0" size={18} label={t('loading')} />}
      {!loading && hasMore && (
        <button type="button" className="ofp-link-btn ofp-more" onClick={() => void load(offset, 10)}>
          {t('comments.more')}
        </button>
      )}
      {!loading && !items.length && <p className="ofp-muted">{t('comments.none')}</p>}
      {canWrite && post.canComment && (
        <form
          className="ofp-comment-form"
          onSubmit={(e) => {
            e.preventDefault()
            void send()
          }}
        >
          <input ref={input} className="ofp-input" aria-label={t('comments.add')} placeholder={t('comments.add')} value={text} onChange={(e) => setText(e.target.value)} />
          <button type="submit" className="lib-icon-btn" aria-label={t('comments.send')} disabled={!text.trim() || sending}>
            <FeatherIcon name="send" size={16} />
          </button>
        </form>
      )}
    </section>
  )
}
